import { cloneDeep, Dictionary, groupBy, sortBy } from 'lodash';
import { FormChoiceOption, FormQuestion, FormTemplate } from 'api/form/types';

export const flattenQuestions = (
  formQuestions: FormQuestion[],
): FormQuestion[] => {
  return cloneDeep(formQuestions).reduce(
    (acc: FormQuestion[], currentQuestion) => {
      let flattenedBranchesList = [] as FormQuestion[];

      if (currentQuestion.rows) {
        const flattenedRows = currentQuestion.rows.map((row: FormQuestion) => {
          return {
            ...row,
            questionText: `[${row.questionText}] ${currentQuestion.questionText}`,
            options: currentQuestion.options,
          };
        });

        acc.push(currentQuestion, ...flattenedRows);

        return acc;
      }

      currentQuestion.options?.forEach((option) => {
        if (option.branches) {
          const flattenedOptionQuestions = flattenQuestions(option.branches);

          flattenedBranchesList = flattenedBranchesList.concat(
            flattenedOptionQuestions,
          );

          // eslint-disable-next-line no-param-reassign
          delete option.branches;
        }
      });

      acc.push(currentQuestion, ...flattenedBranchesList);

      return acc;
    },
    [],
  );
};

export const flattenFormTemplate = (
  formTemplate: FormTemplate,
): FormTemplate => {
  return {
    ...formTemplate,
    formQuestions: flattenQuestions(formTemplate.formQuestions),
  };
};

const parseTreeStructure = (
  groupedFormQuestions: Dictionary<FormQuestion[]>,
  traverseItemId = -1,
  depthLevel = 1,
): FormQuestion[] => {
  return groupedFormQuestions[traverseItemId]
    ? groupedFormQuestions[traverseItemId].map((question) => {
        const updatedQuestion = { ...question };

        if (question.options) {
          const updatedOptions = [] as FormChoiceOption[];

          question.options.forEach((option) => {
            updatedOptions.push({
              ...option,
              branches: parseTreeStructure(
                groupedFormQuestions,
                option.optionId,
                depthLevel + 1,
              ),
            });
          });
          updatedQuestion.options = updatedOptions;
        }
        updatedQuestion.depth = depthLevel;

        return updatedQuestion;
      })
    : [];
};

export const constructTreeStructure = (
  flattenedFormQuestions: FormQuestion[],
): FormQuestion[] => {
  const groupedFormQuestion = groupBy(
    sortBy(flattenedFormQuestions, 'position'),
    'branchingFrom',
  );

  // // questions without any parent branching will be grouped as undefined. Setting to -1 for handling during parsing
  groupedFormQuestion[-1] = groupedFormQuestion.undefined;

  return parseTreeStructure(groupedFormQuestion);
};

export const computeFormBgImageBrightness = (brightness: number): string => {
  const brightnessValue = brightness / 100;
  const brightnessString =
    brightnessValue <= 0.5
      ? `rgba(0, 0, 0, ${Math.abs(0.5 - brightnessValue) * 2})`
      : `rgba(255, 255, 255, ${(brightnessValue - 0.5) * 2})`;

  return brightnessString;
};
